import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getIndustryById } from '@/data/industries'
import { companyInfo } from '@/data/company'

export function CreditUnions() {
  const industry = getIndustryById('credit-unions')!

  return (
    <>
      <SEO
        title="Technology Solutions for Credit Unions"
        description="Purpose-built technology solutions that help credit unions deliver exceptional member experiences while maintaining security and compliance."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">Credit Union Service</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                IT Cost Optimization for <span className="text-gradient">Credit Unions</span>
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                We assist Credit Unions through an impartial, data-backed approach in pinpointing and choosing the optimal contact center technology and operational solutions aligned with your member-service objectives.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get Credit Union Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-6">
                  {industry.stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="text-center"
                    >
                      <div className="text-3xl md:text-4xl font-heading font-bold text-gradient mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-slate-400">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Challenges We Solve</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              We Understand Credit Union Challenges
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Our team has decades of experience helping credit unions overcome technology challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {industry.challenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-warning-500/20 flex items-center justify-center">
                        <span className="text-warning-500 font-bold">{index + 1}</span>
                      </div>
                      {challenge.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {challenge.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Our Solutions</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Technology Built for Credit Unions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {industry.solutions.map((solution, index) => (
              <motion.div
                key={solution.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <solution.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{solution.title}</CardTitle>
                    <CardDescription className="text-base">
                      {solution.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Credit Unions Choose Us */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Why Credit Unions Choose Us</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Your Technology Partner
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                We're more than a vendor—we're a strategic partner committed to your credit union's success.
              </p>

              <div className="space-y-4">
                {[
                  'Deep understanding of credit union operations',
                  'Experience with major core banking integrations',
                  'NCUA compliance expertise',
                  'Dedicated account management',
                  'Flexible, scalable solutions',
                  'Member-focused technology design',
                ].map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Core System Integrations
              </h3>
              <p className="text-slate-400 mb-6">
                Our solutions integrate with the core banking systems credit unions rely on:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {['Symitar', 'Fiserv', 'Corelation', 'CU*Answers', 'DNA', 'Finastra'].map((core) => (
                  <div key={core} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-accent-400" />
                    {core}
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="w-full" variant="glow">
                  Schedule a Demo
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

